import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppComponent } from '../app.component';
import { empty } from '../helpers';
import { LoggerService } from '../logger.service';
import { AddressModel } from '../model/address.model';
import { ApiError } from '../model/api-error';
import { CountryModel } from '../model/country.model';
import { IndividualAddressModel } from '../model/individual-address.model';
import { WizardStep } from '../wizard-step';
import { WizardNavigationService } from '../wizard/wizard-navigation.service';
import { WizardService } from '../wizard/wizard.service';
import { AddressError } from './address-error';
import { AddressService } from './address.service';
import { PhoneComponent } from './phone/phone.component';

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.css'],
  providers: [AddressService]
})
export class AddressComponent implements OnInit, AfterViewInit, WizardStep {

  static readonly Name = 'AddressComponent';
  readonly Name = 'AddressComponent';

  @ViewChild('fieldset') fieldset: ElementRef;

  @ViewChild('inputCountry') inputCountry: ElementRef;

  @ViewChild('msgNoAddress', { static: true }) msgNoAddress: TemplateRef<any>;

  @ViewChild('msgValidated', { static: true }) msgValidated: TemplateRef<any>;

  @ViewChild('form', { static: true }) tplForm: TemplateRef<any>;

  @ViewChild('choose', { static: true }) tplChoose: TemplateRef<any>;

  @ViewChild(PhoneComponent) phoneComponent: PhoneComponent;

  address: AddressModel;
  addressFields: 'FULL' | 'CITY_STATE' = 'FULL';

  show: TemplateRef<any>;

  OnCountryChange: EventEmitter<CountryModel> = new EventEmitter<CountryModel>();

  OnSavePhone: EventEmitter<any> = new EventEmitter<any>();

  error: AddressError = null;

  get valid(): boolean {
    return this.service.group.valid && (this.phoneComponent ? this.phoneComponent.Valid : true);
  }

  get validated(): AddressModel {
    return this.service.validated;
  }

  get original(): AddressModel {
    return this.service.original;
  }

  get isSaving(): boolean {
    return this.service.isSaving;
  }

  set isSaving(state: boolean) {
    this.service.isSaving = state;
    this.app.wait(this.isSaving);
  }

  get countries(): CountryModel[] {
    return this.service.Countries;
  }

  get group(): UntypedFormGroup {
    return this.service.group;
  }

  get showOriginal(): boolean {
    return this.service.original instanceof AddressModel;
  }

  get mustValidate(): boolean {
    if (this.addressFields == 'CITY_STATE') return false;
    if (this.group.value.adr_country) {
      if (this.group.value.adr_country.cty_iso_2 !== 'US') {
        return false;
      }

      if (this.service.validated instanceof AddressModel && this.group.pristine && (this.group.valid || this.group.errors == null)) {
        return false;
      }

      return true;
    }

    return true;
  }

  get canSkip(): boolean {
    return this.address instanceof IndividualAddressModel && !empty(this.address.cxa_key) && !empty(this.wizard.Individual.cxa_key);
  }

  constructor(
    private wizard: WizardService,
    private navigation: WizardNavigationService,
    private route: ActivatedRoute,
    private service: AddressService,
    private logger: LoggerService,
    private changeDetector: ChangeDetectorRef,
    private app: AppComponent
  ) {
    this.isSaving = false;

    if(this.wizard.HasProspectType) {
      switch (this.wizard.ProspectType) {
        case 'instructor':
          this.addressFields = 'CITY_STATE';
          break;
      }
    }
  }

  public ngOnInit(): void {
    this.show = this.tplForm;

    const usa = this.countries.find(cty => cty.cty_iso_2 === 'US');
    const adr_country = new UntypedFormControl(usa, [Validators.required]);

    this.group.registerControl('adr_country', adr_country);

    if (!empty(this.wizard.Individual.cxa_key)) {
      if (this.wizard.Address === null) {
        this.wizard.Address = this.service.GetAddress(this.wizard.Individual.cxa_key);
        this.wizard.Address.subscribe(address => {
          this.wizard.setAddress(address);
        });
      }

      if (this.wizard.Address instanceof Observable) {
        this.wizard.Address.subscribe(address => {
          this.isSaving = true;
          this.address = address;
          this.group.reset(address);

          const country = this.countries.find((cty: CountryModel) => cty.cty_code === address.adr_country);
          this.group.get('adr_country').reset(country, { emitEvent: false });
        });
      }
    }
  }

  public ngAfterViewInit(): void {
  }

  edit(address: AddressModel) {
    this.error = null;
    this.show = this.tplForm;
    this.address = address;
    this.service.validated = null;
  }

  OnSubmit() {
    this.isSaving = true;

    this.changeDetector.detectChanges();

    this.app.scrollUp();

    if (this.mustValidate) {
      this.service.Validate()
        .subscribe(
          result => this.onValidate(result),
          err => this.onError(err),
          () => {
            this.isSaving = false;
          }
        );
    } else {
      if (this.service.validated) {
        this.OnSavePhone.emit(this.service.validated);
        this.service.SaveAddress(this.service.validated).subscribe(result => {
          this.wizard.setAddress(result);
          this.navigation.next(this, this.route);
        });
      } else {
        const address = this.service.Form2Model(this.group.value);

        this.OnSavePhone.emit(address);
        this.service.SaveAddress(address).subscribe(result => {
          this.wizard.setAddress(result);
          this.navigation.next(this, this.route);
        });
      }
    }
  }

  OnContinue() {
    if (this.address && this.address instanceof AddressModel) {
      this.isSaving = true;
      this.changeDetector.detectChanges();
      this.OnSavePhone.emit(this.address);
      this.service.SaveAddress(this.address).pipe(
        switchMap(result => this.wizard.SyncIndividual().pipe(map(() => result))),
      ).subscribe(result => {
        this.wizard.setAddress(result);
        this.navigation.next(this, this.route);
      });
    }
  }

  OnUseOriginal() {
    this.isSaving = true;
    this.changeDetector.detectChanges();
    this.OnSavePhone.emit(this.service.original);
    this.service.SaveAddress(this.service.original).pipe(
      switchMap(result => this.wizard.SyncIndividual().pipe(map(() => result))),
    ).subscribe(result => {
      this.wizard.setAddress(result);
      this.navigation.next(this, this.route);
    });
  }

  OnUseUnvalidated() {
    this.isSaving = true;
    this.changeDetector.detectChanges();
    const address = this.service.Form2Model(this.group.value);
    this.OnSavePhone.emit(address);
    this.service.SaveAddress(address).pipe(
      switchMap(result => this.wizard.SyncIndividual().pipe(map(() => result))),
    ).subscribe(result => {
      this.wizard.setAddress(result);
      this.navigation.next(this, this.route);
    });
  }

  OnSkip() {
    this.isSaving = true;
    this.changeDetector.detectChanges();
    this.OnSavePhone.emit(this.address);
    this.wizard.setAddress(this.address);
    this.navigation.next(this, this.route);
  }

  onError(error: Error) {
    try {
      this.logger.log(error);
    } catch { }

    if (error instanceof ApiError) {
      switch (error.code) {
        case 'not-found':
          this.error = new AddressError(this.msgNoAddress, 'warning');
          break;
        default:
          break;
      }
    }

    this.isSaving = false;
  }

  onValidate(address: IndividualAddressModel | AddressModel) {
    if (this.address instanceof IndividualAddressModel) {
      address = Object.assign(this.address, address);
    }

    this.OnSavePhone.emit(address);

    this.group.reset(address);

    this.group.get('adr_country').reset(this.countries.find((cty: CountryModel) => cty.cty_code === address.adr_country));

    const adr_state = this.service.GetAdministrativeArea(address.adr_country, address.adr_state);
    if (adr_state) {
      this.group.get('adr_state').reset(adr_state);
    }

    this.show = this.tplChoose;
    this.address = address;

    this.error = new AddressError(this.msgValidated, 'info');
  }

  ready(): boolean {
    return true;
  }

}
